import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../ToastProvider';
import { formatCurrency, formatDate } from '../../utils/calculations';

const CustomerProfile = ({ phoneNumber, restaurantId }) => {
    const [profile, setProfile] = useState(null);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const addToast = useToast();

    useEffect(() => {
        if (phoneNumber) {
            fetchProfile();
            fetchInsights();
        }
    }, [phoneNumber, restaurantId]);

    const fetchProfile = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.CUSTOMER_PROFILE(phoneNumber));
            setProfile(response.data);
            setEditForm(response.data);
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            // If profile doesn't exist, create a basic one
            setProfile({
                name: '',
                phoneNumber,
                email: '',
                preferences: {
                    dietaryRestrictions: [],
                    favoriteItems: []
                },
                loyaltyPoints: 0,
                totalOrders: 0,
                totalSpent: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchInsights = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.CUSTOMER_INSIGHTS(phoneNumber, restaurantId));
            setInsights(response.data);
        } catch (error) {
            console.error('Failed to fetch insights:', error);
        }
    };

    const handleSaveProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.put(API_ENDPOINTS.CUSTOMER_PROFILE_UPDATE(phoneNumber), editForm);
            setProfile(response.data);
            setEditing(false);
            addToast('Profile updated successfully!', 'success');
        } catch (error) {
            addToast('Failed to update profile', 'error');
            console.error('Failed to update profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const addToFavorites = async (itemId) => {
        try {
            await axios.post(API_ENDPOINTS.CUSTOMER_ADD_FAVORITE(phoneNumber), { itemId });
            fetchProfile();
            addToast('Added to favorites!', 'success');
        } catch (error) {
            addToast('Failed to add to favorites', 'error');
        }
    };

    const removeFromFavorites = async (itemId) => {
        try {
            await axios.delete(API_ENDPOINTS.CUSTOMER_REMOVE_FAVORITE(phoneNumber), { data: { itemId } });
            fetchProfile();
            addToast('Removed from favorites!', 'success');
        } catch (error) {
            addToast('Failed to remove from favorites', 'error');
        }
    };

    if (loading && !profile) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Profile Header */}
            <motion.div 
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 border border-slate-200 rounded-full flex items-center justify-center text-3xl">
                            👤
                        </div>
                        <div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
                                {profile?.name || 'Customer'}
                            </h2>
                            <p className="text-stone-600">{profile?.phoneNumber}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-sm text-stone-700">
                                    {profile?.customerTier || 'Bronze'} Member
                                </span>
                                <span className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-sm text-stone-700">
                                    {profile?.loyaltyPoints || 0} Points
                                </span>
                            </div>
                        </div>
                    </div>
                    <motion.button
                        onClick={() => setEditing(!editing)}
                        className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {editing ? '✕ Cancel' : '✏️ Edit Profile'}
                    </motion.button>
                </div>
            </motion.div>

            {/* Profile Stats */}
            {insights && (
                <motion.div 
                    className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    {[
                        { label: 'Total Orders', value: insights.totalOrders, icon: '📋', color: 'from-green-400 to-green-600' },
                        { label: 'Total Spent', value: formatCurrency(insights.totalSpent), icon: '💰', color: 'from-blue-400 to-blue-600' },
                        { label: 'Average Order', value: formatCurrency(insights.averageOrderValue), icon: '📊', color: 'from-purple-400 to-purple-600' },
                        { label: 'Days as Customer', value: insights.daysSinceFirstVisit, icon: '📅', color: 'from-orange-400 to-orange-600' }
                    ].map((stat, index) => (
                        <motion.div
                            key={stat.label}
                            className="bg-stone-50 border border-stone-200 rounded-2xl p-4"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-stone-500 text-xs font-semibold uppercase tracking-wide">
                                        {stat.label}
                                    </p>
                                    <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
                                </div>
                                <div className="text-3xl">{stat.icon}</div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            )}

            {/* Profile Details */}
            <motion.div 
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-stone-900">Profile Details</h3>
                
                {editing ? (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-stone-700 mb-2">Full Name</label>
                                <input
                                    type="text"
                                    value={editForm.name || ''}
                                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                    className="w-full border border-stone-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={editForm.email || ''}
                                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                    className="w-full border border-stone-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-stone-700 mb-2">Dietary Restrictions</label>
                            <div className="flex flex-wrap gap-2">
                                {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut-Free'].map((restriction) => (
                                    <button
                                        key={restriction}
                                        onClick={() => {
                                            const current = editForm.preferences?.dietaryRestrictions || [];
                                            const updated = current.includes(restriction)
                                                ? current.filter(r => r !== restriction)
                                                : [...current, restriction];
                                            setEditForm({
                                                ...editForm,
                                                preferences: {
                                                    ...editForm.preferences,
                                                    dietaryRestrictions: updated
                                                }
                                            });
                                        }}
                                        className={`px-4 py-2 rounded-xl border transition-all duration-300 ${
                                            editForm.preferences?.dietaryRestrictions?.includes(restriction)
                                                ? 'bg-blue-500 text-white border-blue-500'
                                                : 'bg-white text-stone-700 border-stone-200 hover:border-blue-300'
                                        }`}
                                    >
                                        {restriction}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex space-x-4">
                            <motion.button
                                onClick={handleSaveProfile}
                                disabled={loading}
                                className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {loading ? 'Saving...' : '💾 Save Changes'}
                            </motion.button>
                            <motion.button
                                onClick={() => setEditing(false)}
                                className="bg-white hover:bg-stone-50 border border-stone-200 text-stone-800 px-8 py-3 rounded-xl font-semibold transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Cancel
                            </motion.button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Full Name</label>
                                <p className="text-lg font-semibold text-stone-900">{profile?.name || 'Not provided'}</p>
                            </div>
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                                <p className="text-lg font-semibold text-stone-900">{profile?.email || 'Not provided'}</p>
                            </div>
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Customer Since</label>
                                <p className="text-lg font-semibold text-stone-900">
                                    {profile?.createdAt ? formatDate(profile.createdAt) : 'Today'}
                                </p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Dietary Restrictions</label>
                                <div className="flex flex-wrap gap-2">
                                    {profile?.preferences?.dietaryRestrictions?.length > 0 ? (
                                        profile.preferences.dietaryRestrictions.map((restriction) => (
                                            <span key={restriction} className="px-3 py-1 bg-stone-100 text-stone-800 rounded-full text-sm border border-stone-200">
                                                {restriction}
                                            </span>
                                        ))
                                    ) : (
                                        <p className="text-stone-500">None specified</p>
                                    )}
                                </div>
                            </div>
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Loyalty Points</label>
                                <p className="text-lg font-semibold text-stone-900">{profile?.loyaltyPoints || 0} points</p>
                            </div>
                            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <label className="block text-sm font-medium text-stone-700 mb-2">Member Tier</label>
                                <p className="text-lg font-semibold text-stone-900">{profile?.customerTier || 'Bronze'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Favorite Items */}
            {profile?.preferences?.favoriteItems?.length > 0 && (
                <motion.div 
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-stone-900">Favorite Items</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {profile.preferences.favoriteItems.map((item) => (
                            <div key={item._id} className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                                <div className="flex items-center space-x-3">
                                    {item.itemImage && (
                                        <img src={item.itemImage} alt={item.itemName} className="w-12 h-12 rounded-lg object-cover" />
                                    )}
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-stone-800">{item.itemName}</h4>
                                        <p className="text-stone-600">{formatCurrency(item.itemPrice)}</p>
                                    </div>
                                    <button
                                        onClick={() => removeFromFavorites(item._id)}
                                        className="text-red-500 hover:text-red-700 transition-colors"
                                    >
                                        ❤️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Favorite Items from Insights */}
            {insights?.favoriteItems?.length > 0 && (
                <motion.div 
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-stone-900">Most Ordered Items</h3>
                    <div className="space-y-3">
                        {insights.favoriteItems.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                                        {index + 1}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-stone-800">{item.name}</h4>
                                        <p className="text-stone-600">Ordered {item.count} times</p>
                                    </div>
                                </div>
                                <div className="text-2xl">
                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default CustomerProfile;